// Pure adapter — contract envelope (data.patients[], data.kpis) → the row shape
// that PatientListTable + useDashboardTable + AccessDashboard consume.

import { SPRUCE_SOURCE } from './chatMessage';

export interface DashboardEnvelope {
  status: 'success' | 'error';
  data?: {
    patients?: DashboardPatientRow[];
    kpis?: DashboardKpisContract;
    columns?: unknown[];
    filters?: unknown[];
  };
  // `include` echoes the opt-ins apiv2 honoured (oap, roster); absent on an apiv2 that predates them.
  meta?: { count?: number; offset?: number; limit?: number; include?: unknown };
  error?: { message?: string };
}

export interface DashboardPatientRow {
  // apiv2 clinical_view.getdashboard emits the patient id as `id_dev_user` (its
  // native key); the contract name is `withings_user_id`. Same value — accept both.
  withings_user_id?: string | number;
  id_dev_user?: string | number;
  first_name?: string;
  last_name?: string;
  phone?: string | null;
  care_period_status?: string;
  start_date?: string;
  end_date?: string;
  ehr_patient_id?: string | null;
  alert_level?: string;
  alert_reason?: string;
  alert_template_code?: string;
  top_task_title?: string;
  top_template_code?: string;
  top_task_priority?: string;
  top_is_overdue?: boolean;
  top_priority?: string;
  overdue_count?: number;
  conditions?: string[];
  last_reading_epoch?: number | null;
  last_systolic?: number | null;
  last_diastolic?: number | null;
  first_systolic?: number | null;
  first_diastolic?: number | null;
  last_bp_epoch?: number | null;
  open_tasks?: number;
  urgent_count?: number;
  high_count?: number;
  plan_updated_at?: string | null;
  plan_created_at?: string | null;
  start_date_iso?: string | null;
  last_human_outreach_at?: string | null;
  last_ai_outreach_at?: string | null;
  risk_level?: string;
  medication_classes?: string[];
  has_unread?: boolean;
  last_inbound_snippet?: string | null;
  last_inbound_source?: string | null;
  // CMS outcome, only on a `clinical_include=oap` request to an apiv2 that computes it.
  oap_outcome?: string | null;
  oap_reason?: string | null;
  oap_met?: number | null;
  oap_required?: number | null;
  // Roster details, only on a `clinical_include=roster` request to an apiv2 that computes them.
  subtrack?: unknown;
  next_due?: unknown;
  visit?: unknown;
}

interface DashboardKpisContract {
  enrollment?: { total?: number };
}

export interface LegacyDashboardRow {
  userid: string | number;
  name: { first: string; last: string };
  phone_number: string | null;
  care_period_status?: string;
  start_date?: string;
  end_date?: string;
  ehr_patient_id?: string | null;
  alert: { level?: string; reason?: string; template_code?: string };
  task: { title?: string; template_code?: string; priority?: string; is_overdue?: boolean };
  overdue: { count: number };
  conditions: { list: string[] };
  lastReading: { epoch: number | null };
  bp: {
    systolic: number | null;
    diastolic: number | null;
    baseline_systolic: number | null;
    baseline_diastolic: number | null;
    last_bp_epoch: number | null;
  };
  tasks: { count: number; top_priority?: string; urgent: number; high: number };
  gap: { value: number; on_target: boolean } | null;
  med: { date: string | null };
  plan: { updated_at: string | null; created_at: string | null };
  lastHumanOutreach: { at: string | null };
  lastAiOutreach: { at: string | null };
  enrolled: { date: string | null; days_in_program: number | null };
  risk_level?: string;
  medication_classes?: string[];
  unread: { has_unread: boolean; snippet: string | null; source?: string | null };
  oap: { outcome: string | null; reason: string | null; met: number | null; required: number | null };
  subtrack: string | null;
  nextDue: RosterNextDue | null;
  visit: RosterVisit;
}

export interface RosterNextDue {
  title: string | null;
  status: string | null;
  dueAt: string | null;
  appointment: { state: string; scheduled_at: string | null } | null;
}
export interface RosterVisitStamp { at: string; kind: string | null; state: string | null }
export interface RosterVisit { last: RosterVisitStamp | null; next: RosterVisitStamp | null }

export interface LegacyDashboardKpis {
  enrolled: { count: number };
}

export interface LegacyDashboardShape {
  status: 'success' | 'error';
  role?: string;
  kpis?: LegacyDashboardKpis;
  columns?: unknown[];
  filters?: unknown[];
  rows?: LegacyDashboardRow[];
  // The opt-ins apiv2 honoured for this response; [] on an apiv2 that predates the echo.
  includes?: string[];
  pagination?: { total_count: number; offset: number; limit: number; has_more: boolean };
  reason?: string;
}

// Per-row BP gap. last - max(first-15, 130). null if either is missing.
function computeGap(lastSys: number | null, firstSys: number | null): number | null {
  if (lastSys === null || firstSys === null) return null;
  return lastSys - Math.max(firstSys - 15, 130);
}

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const isoOrNull = (v: unknown): string | null => (typeof v === 'string' && Number.isFinite(Date.parse(v)) ? v : null);

// Each roster reader fails closed: anything not in the pinned shape reads as "none".
function readSubtrack(v: unknown): string | null {
  return v && typeof v === 'object' ? str((v as { id?: unknown }).id) : null;
}

function readNextDue(v: unknown): RosterNextDue | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const n = v as Record<string, unknown>;
  const a = n.appointment;
  const appointment = a && typeof a === 'object' && str((a as { state?: unknown }).state)
    ? { state: String((a as { state: unknown }).state), scheduled_at: isoOrNull((a as { scheduled_at?: unknown }).scheduled_at) }
    : null;
  return {
    title: str(n.title), status: str(n.status), dueAt: isoOrNull(n.due_at), appointment,
  };
}

function readVisitStamp(v: unknown): RosterVisitStamp | null {
  if (!v || typeof v !== 'object') return null;
  const s = v as Record<string, unknown>;
  const at = isoOrNull(s.at);
  return at ? { at, kind: str(s.kind), state: str(s.state) } : null;
}

function readVisit(v: unknown): RosterVisit {
  const x = v && typeof v === 'object' ? v as { last?: unknown; next?: unknown } : {};
  return { last: readVisitStamp(x.last), next: readVisitStamp(x.next) };
}

function adaptRow(p: DashboardPatientRow, nowMs: number): LegacyDashboardRow {
  const lastSys = p.last_systolic ?? null;
  const firstSys = p.first_systolic ?? null;
  const gap = computeGap(lastSys, firstSys);
  const enrolledStartIso = p.plan_created_at || p.start_date || null;
  const daysInProgram = enrolledStartIso
    ? Math.max(0, Math.floor((nowMs - new Date(enrolledStartIso).getTime()) / 86400000))
    : null;

  return {
    userid: p.withings_user_id ?? p.id_dev_user ?? '', // '' = id-less row, dropped by adaptDashboardEnvelope (unselectable)
    name: { first: p.first_name || '', last: p.last_name || '' },
    phone_number: p.phone || null,
    care_period_status: p.care_period_status,
    start_date: p.start_date,
    end_date: p.end_date,
    ehr_patient_id: p.ehr_patient_id,
    alert: { level: p.alert_level, reason: p.alert_reason, template_code: p.alert_template_code },
    task: {
      title: p.top_task_title,
      template_code: p.top_template_code,
      priority: p.top_task_priority,
      is_overdue: p.top_is_overdue,
    },
    overdue: { count: p.overdue_count ?? 0 },
    conditions: { list: p.conditions || [] },
    lastReading: { epoch: p.last_reading_epoch ?? null },
    bp: {
      systolic: lastSys,
      diastolic: p.last_diastolic ?? null,
      baseline_systolic: firstSys,
      baseline_diastolic: p.first_diastolic ?? null,
      last_bp_epoch: p.last_bp_epoch ?? null,
    },
    tasks: {
      count: p.open_tasks ?? 0,
      top_priority: p.top_priority,
      urgent: p.urgent_count ?? 0,
      high: p.high_count ?? 0,
    },
    gap: gap === null ? null : { value: gap, on_target: gap <= 0 },
    med: { date: p.plan_updated_at || null },
    plan: { updated_at: p.plan_updated_at || null, created_at: p.plan_created_at || null },
    lastHumanOutreach: { at: p.last_human_outreach_at || null },
    lastAiOutreach: { at: p.last_ai_outreach_at || null },
    enrolled: { date: enrolledStartIso, days_in_program: daysInProgram },
    risk_level: p.risk_level,
    medication_classes: p.medication_classes,
    unread: { has_unread: !!p.has_unread, snippet: p.last_inbound_snippet || null, source: p.last_inbound_source || null },
    oap: {
      outcome: p.oap_outcome || null,
      reason: p.oap_reason || null,
      met: typeof p.oap_met === 'number' ? p.oap_met : null,
      required: typeof p.oap_required === 'number' ? p.oap_required : null,
    },
    subtrack: readSubtrack(p.subtrack),
    nextDue: readNextDue(p.next_due),
    visit: readVisit(p.visit),
  };
}

type RowUnread = LegacyDashboardRow['unread'] | null | undefined;

// The message the preview quotes came in through the clinic phone line, which anyone can text.
export function lastInboundViaClinicLine(unread: RowUnread): boolean {
  return unread?.source === SPRUCE_SOURCE;
}

// Engage dates a patient's message from the engagement-state cache, which only holds app messages.
export function lastInboundDatable(unread: RowUnread): boolean {
  const source = unread?.source;
  return !source || source === 'patient';
}

function adaptKpis(k: DashboardKpisContract): LegacyDashboardKpis {
  return { enrolled: { count: k.enrollment?.total ?? 0 } };
}

const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object';

// Read a single option's pre-computed count out of the dashboard `filters[]`
// envelope (shape owned by apiv2's dashboard view PU).
// Returns 0 when the filter, option, or count is absent.
export function filterOptionCount(
  filters: unknown[] | undefined,
  key: string,
  value: string,
): number {
  if (!Array.isArray(filters)) return 0;
  const f = filters.find((x) => isObj(x) && x.key === key) as { options?: unknown[] } | undefined;
  if (!f || !Array.isArray(f.options)) return 0;
  const opt = f.options.find((o) => isObj(o) && o.value === value) as { count?: number } | undefined;
  return typeof opt?.count === 'number' ? opt.count : 0;
}

// The `open_task` facet (apiv2 dashboard view): one option per open task type, plus one
// group per ACCESS journey step — 15 of them, every request, in journey order, `other` last.
export interface OpenTaskOption { value: string; label: string; count?: number; group: string | null }
export interface OpenTaskGroup { value: string; label: string; count: number }
export interface OpenTaskFacet {
  key: 'open_task';
  label: string;
  semantics: 'and' | null;
  options: OpenTaskOption[];
  groups: OpenTaskGroup[];
}

export const OTHER_GROUP = 'other';
// One glyph per journey step — decorative, so every renderer hides it from screen readers.
const STEP_EMOJI: Record<string, string> = {
  enrollment: '\u{1F4DD}',
  labs: '\u{1F9EA}',
  visit: '\u{1FA7A}',
  alignment: '\u{1F91D}',
  devices: '\u{1F4E6}',
  baseline: '\u{1F4CA}',
  pcp: '\u{1F4E8}',
  monthly: '\u{1F5D3}\uFE0F',
  quarterly: '\u{1F4C8}',
  medication: '\u{1F48A}',
  reconciliation: '\u{1F9FE}',
  final: '\u{1F3C1}',
  followon: '\u{1F501}',
  billing: '\u{1F4B5}',
  other: '\u{1F9E9}',
};
// A step the client does not know yet still gets a glyph.
export const stepEmoji = (value: string): string => STEP_EMOJI[value] ?? '\u{1F539}';

// Null when the facet is absent or predates journey groups — the strip renders nothing then.
export function openTaskFacet(filters: unknown[] | undefined): OpenTaskFacet | null {
  if (!Array.isArray(filters)) return null;
  const f = filters.find((x) => isObj(x) && x.key === 'open_task');
  if (!isObj(f) || !Array.isArray(f.groups)) return null;
  const groups: OpenTaskGroup[] = f.groups.filter(isObj).map((g) => ({
    value: String(g.value),
    label: typeof g.label === 'string' ? g.label : String(g.value),
    count: typeof g.count === 'number' ? g.count : 0,
  }));
  const options: OpenTaskOption[] = (Array.isArray(f.options) ? f.options : []).filter(isObj).map((o) => ({
    value: String(o.value),
    label: typeof o.label === 'string' ? o.label : String(o.value),
    count: typeof o.count === 'number' ? o.count : undefined,
    group: typeof o.group === 'string' ? o.group : null,
  }));
  return {
    key: 'open_task',
    label: typeof f.label === 'string' ? f.label : 'Open task',
    semantics: f.semantics === 'and' ? 'and' : null,
    options,
    groups,
  };
}

export function journeyGroups(filters: unknown[] | undefined): OpenTaskGroup[] {
  return openTaskFacet(filters)?.groups ?? [];
}

// ── Board picks: what a tile, card or chip click does to the selected filters ──
type Selected = Record<string, string[]>;

// The Patients tile sets the scope, which every other pick keeps: the server counts the chips within it.
export const SCOPE_KEY = 'lifecycle';
export const BOARD_PICK_KEYS = ['alert', 'engagement', 'open_task', 'oap_outcome', 'report_ready'] as const;
// The only value `report_ready` takes: apiv2 refuses anything else.
export const REPORT_READY_VALUE = '1';
// Diagnosis-level filters never ride on a URL that names a patient (PHI next to an id).
export const SENSITIVE_BOARD_KEYS = ['subtrack', 'conditions'] as const;
export type BoardPickKey = typeof BOARD_PICK_KEYS[number];
export type BoardPick = { key: typeof SCOPE_KEY; value: string | null } | { key: BoardPickKey; value: string };

const sameList = (a: string[] = [], b: string[] = []): boolean => a.length === b.length && a.every((v, i) => v === b[i]);

// Same object back when nothing changes, so the caller can skip the refetch.
function withValues(selected: Selected, key: string, values: string[]): Selected {
  if (sameList(selected[key], values)) return selected;
  const rest = Object.fromEntries(Object.entries(selected).filter(([k]) => k !== key));
  return values.length ? { ...rest, [key]: values } : rest;
}

export function isPicked(selected: Selected, key: string, value: string): boolean {
  return (selected[key] ?? []).includes(value);
}

// The Patients tile's checked segment: null = enrolled; undefined = a toolbar mix no segment names.
export function scopeOf(selected: Selected): string | null | undefined {
  const cur = selected[SCOPE_KEY] ?? [];
  if (cur.length === 0) return null;
  return cur.length === 1 ? cur[0] : undefined;
}

export function applyBoardPick(selected: Selected, pick: BoardPick): Selected {
  if (pick.key === SCOPE_KEY) {
    const again = pick.value !== null && sameList(selected[SCOPE_KEY], [pick.value]);
    return withValues(selected, SCOPE_KEY, pick.value === null || again ? [] : [pick.value]);
  }
  const cur = selected[pick.key] ?? [];
  if (cur.includes(pick.value)) return withValues(selected, pick.key, cur.filter((v) => v !== pick.value));
  const kept = Object.entries(selected).filter(([k]) => !(BOARD_PICK_KEYS as readonly string[]).includes(k));
  return { ...Object.fromEntries(kept), [pick.key]: [pick.value] };
}

// Null when apiv2 sent no report_ready facet: the chip and its To do say nothing then.
export function reportReadyCount(filters: unknown[] | undefined): number | null {
  if (!Array.isArray(filters)) return null;
  const f = filters.find((x) => isObj(x) && x.key === 'report_ready');
  return isObj(f) && typeof f.count === 'number' ? f.count : null;
}

// ── ACCESS journey rows ──
export interface JourneyChip {
  value: string;
  // The board pick a click sends: open_task for the journey steps, report_ready for Ready to submit.
  pickKey: 'open_task' | 'report_ready';
  pickValue: string;
  label: string;
  emoji: string;
  count: number;
  urgent: boolean;
}
export interface JourneyRow { id: string; label: string; chips: JourneyChip[] }

interface ChipDef {
  value: string;
  // 'ready' counts from apiv2's report_ready facet, not the open_task facet, and is drawn only with it.
  kind: 'group' | 'option' | 'ready';
  label: string;
  todo: string;
  emoji?: string;
  urgent?: boolean;
  occasional?: boolean; // drawn only when above 0 or selected
  coordinatorOnly?: boolean; // a clinician sees it only when above 0 (an escalated task) or selected
}

const g = (value: string, label: string, todo: string, extra: Partial<ChipDef> = {}): ChipDef => ({
  value, kind: 'group', label, todo, ...extra,
});
const o = (value: string, label: string, todo: string, extra: Partial<ChipDef> = {}): ChipDef => ({
  value, kind: 'option', label, todo, ...extra,
});

const CMS = { coordinatorOnly: true };
const READY_CHIP = 'report_ready';
const JOURNEY_LAYOUT: { id: string; label: string; chips: ChipDef[] }[] = [
  {
    id: 'onboarding',
    label: 'Onboarding',
    chips: [
      g('enrollment', 'Enrollment', 'Finish the enrollment checks: consent, eligibility, intake and the care plan.'),
      g('labs', 'Labs', 'Sign the baseline lab order in Elation, then review the results.'),
      g('visit', 'Visit', 'Schedule or hold the patient’s virtual visits.'),
      g('alignment', 'CMS alignment', 'Submit the patient’s CMS alignment.'),
      g('devices', 'Devices', 'Ship the patient’s devices.'),
      g('pcp', 'PCP sharing', 'Share the reviewed care plan with the patient’s PCP.'),
    ],
  },
  {
    id: 'reporting',
    label: 'CMS reporting',
    chips: [
      g('baseline', 'Baseline to file', 'File the baseline report with CMS by day 60 after alignment; the task says what is still missing.', { emoji: '\u{1F4C4}', ...CMS }),
      {
        value: READY_CHIP, kind: 'ready', label: 'Ready to submit', todo: 'File the report now: the latest daily check found everything CMS needs.', emoji: '\u2705', ...CMS,
      },
      o('rec_billing_blocked_baseline', 'Baseline past day 60', 'No monthly claim until the baseline is filed. File it now, or review the patient for unalignment.', { emoji: '⛔', urgent: true, ...CMS }),
      g('quarterly', 'Quarterly report to file', 'CMS takes it 70 to 110 days after the last report, with 3 cuff readings on one day in the 15 days before filing and a weigh-in.', CMS),
      g('final', 'End-of-period report', 'CMS takes the end-of-period report from day 365 to day 425 after alignment.', { occasional: true, ...CMS }),
      g('reconciliation', 'Reconciliation', 'Prepare the semi-annual reconciliation.', { occasional: true, ...CMS }),
      g('followon', 'Follow-on', 'Ask the patient about a follow-on period, then reassess.', { occasional: true, ...CMS }),
    ],
  },
  {
    id: 'care',
    label: 'Care & billing',
    chips: [
      g('monthly', 'Monthly care', 'Hold the monthly care review and the patient check-in.'),
      g('medication', 'Medication review', 'Review the patient’s medications.'),
      o('rec_monthly_claims', 'G-code claims to file', 'File the month’s G-code claim in the billing system, then mark the task Done. Medicare’s limit: 90 days from the date of service.', { emoji: '\u{1F4B5}', ...CMS }),
    ],
  },
];
const OTHER_CHIP = g(OTHER_GROUP, 'Other', 'Alerts and tasks outside the journey, like a BP crisis or missed readings.');
// `billing` is drawn as its two codes, never as the group.
const SPLIT_GROUP = 'billing';

const CHIP_DEFS = new Map(
  [...JOURNEY_LAYOUT.flatMap((r) => r.chips), OTHER_CHIP].map((d) => [d.value, d]),
);
export const PLACED_JOURNEY_GROUPS = [
  ...[...CHIP_DEFS.values()].filter((d) => d.kind === 'group').map((d) => d.value),
  SPLIT_GROUP,
];

export function journeyRows(
  filters: unknown[] | undefined,
  role: string | null | undefined,
  selected: string[] = [],
  selectedReady: string[] = [],
): JourneyRow[] {
  const facet = openTaskFacet(filters);
  if (!facet) return [];
  const clinician = role === 'clinician';
  const groupCount = new Map(facet.groups.map((x) => [x.value, x.count]));
  const optionCount = new Map(facet.options.map((x) => [x.value, x.count ?? 0]));
  const shown = (count: number, value: string, d: Partial<ChipDef>): boolean => {
    if (count > 0 || selected.includes(value)) return true;
    return !d.occasional && !(d.coordinatorOnly && clinician);
  };
  const readyCount = reportReadyCount(filters);
  const readyOn = selectedReady.includes(REPORT_READY_VALUE);
  const toChip = (d: ChipDef): JourneyChip | null => {
    if (d.kind === 'ready') {
      if (readyCount === null) return null;
      if (!readyOn && !shown(readyCount, '', d)) return null;
      return {
        value: d.value, pickKey: 'report_ready', pickValue: REPORT_READY_VALUE, label: d.label, emoji: d.emoji ?? stepEmoji(d.value), count: readyCount, urgent: false,
      };
    }
    const count = (d.kind === 'group' ? groupCount.get(d.value) : optionCount.get(d.value)) ?? 0;
    if (!shown(count, d.value, d)) return null;
    return {
      value: d.value, pickKey: 'open_task', pickValue: d.value, label: d.label, emoji: d.emoji ?? stepEmoji(d.value), count, urgent: !!d.urgent,
    };
  };
  // Work the client does not know yet still gets a chip, so it never drops off the board.
  const extras: JourneyChip[] = [
    ...facet.groups.filter((x) => !PLACED_JOURNEY_GROUPS.includes(x.value)),
    ...facet.options.filter((x) => x.group === SPLIT_GROUP && !CHIP_DEFS.has(x.value)),
  ].flatMap((x) => {
    const count = x.count ?? 0;
    if (!shown(count, x.value, { occasional: true })) return [];
    return [{
      value: x.value, pickKey: 'open_task' as const, pickValue: x.value, label: x.label, emoji: stepEmoji(x.value), count, urgent: false,
    }];
  });
  const other = toChip(OTHER_CHIP);
  return JOURNEY_LAYOUT.map((r) => {
    const chips = r.chips.map(toChip).filter((c): c is JourneyChip => c !== null);
    if (r.id === 'care') chips.push(...extras, ...(other ? [other] : []));
    return { id: r.id, label: r.label, chips };
  }).filter((r) => r.chips.length > 0);
}

// ── CMS goals cards (drawn once apiv2 returns the `oap_outcome` facet) ──
// The board's fetches ask for it; Engage and the unread badge never do.
export const BOARD_INCLUDE = 'oap,roster';
export const OAP_OUTCOMES = ['met', 'improving', 'not_improving', 'missing', 'no_baseline', 'filed'] as const;
export type OapOutcome = typeof OAP_OUTCOMES[number];
export type OapTone = 'good' | 'info' | 'moderate' | 'high' | 'undefined' | 'none';
export const OAP_OUTCOME_LABELS: Record<OapOutcome, { label: string; sub: string; tone: OapTone; todo: string }> = {
  met: {
    label: 'All goals met', sub: 'keep the current plan', tone: 'good', todo: 'Meets every CMS goal of their track today: keep the current plan.',
  },
  improving: {
    label: 'Improving', sub: 'moving toward the goal', tone: 'info', todo: 'Short of a goal, but below the filed baseline: keep going.',
  },
  not_improving: {
    label: 'Not improving', sub: 'review the treatment', tone: 'moderate', todo: 'Short of a goal and not below the filed baseline: review the treatment or the coaching.',
  },
  missing: {
    label: 'Missing a value', sub: 'get a reading or a lab', tone: 'undefined', todo: 'A goal value is missing or too old: get a reading or order the lab.',
  },
  no_baseline: {
    label: 'No target yet', sub: 'baseline not filed yet', tone: 'undefined', todo: 'No baseline filed with CMS yet, so nothing to measure against: file the baseline.',
  },
  filed: {
    label: 'Filed', sub: 'verdict from CMS later', tone: 'none', todo: 'End-of-period report filed; CMS gives its verdict later.',
  },
};

export interface OapOutcomeCard { value: OapOutcome; label: string; sub: string; tone: OapTone; count: number }

function facetOptions(filters: unknown[] | undefined, key: string): { value: string; label?: string; count?: number }[] | null {
  if (!Array.isArray(filters)) return null;
  const f = filters.find((x) => isObj(x) && x.key === key);
  if (!isObj(f) || !Array.isArray(f.options)) return null;
  return f.options.filter(isObj).map((x) => ({
    value: String(x.value),
    label: typeof x.label === 'string' ? x.label : undefined,
    count: typeof x.count === 'number' ? x.count : undefined,
  }));
}

// Null without the facet: an apiv2 before the CMS outcome, or a request without the opt-in.
export function oapOutcomeCards(filters: unknown[] | undefined, selected: string[] = []): OapOutcomeCard[] | null {
  const options = facetOptions(filters, 'oap_outcome');
  if (!options) return null;
  const countOf = (v: string) => options.find((x) => x.value === v)?.count ?? 0;
  return OAP_OUTCOMES
    .filter((v) => v !== 'filed' || countOf(v) > 0 || selected.includes(v))
    .map((v) => ({ value: v, ...OAP_OUTCOME_LABELS[v], count: countOf(v) }));
}

const REASON_MEASURE: Record<string, string> = {
  bp: 'BP', weight: 'Weight', hba1c: 'HbA1c', ldl_c: 'LDL-C',
};
const REASON_STATE: Record<string, string> = {
  not_met: 'not met', improving: 'improving', missing: 'no value', expired: 'value too old',
};
const REASON_WHOLE: Record<string, string> = {
  on_target: 'Every goal met', baseline_due: 'Baseline not filed', filed: 'End-of-period report filed',
};

// The deciding measure, in words; null for a code the client does not know.
export function oapReasonText(code: string | null | undefined): string | null {
  if (!code) return null;
  if (REASON_WHOLE[code]) return REASON_WHOLE[code];
  const m = Object.keys(REASON_MEASURE).find((k) => code.startsWith(`${k}_`) && REASON_STATE[code.slice(k.length + 1)]);
  return m ? `${REASON_MEASURE[m]}: ${REASON_STATE[code.slice(m.length + 1)]}` : null;
}

// ── The "To do" line under the toolbar ──
const TILE_TODO: Record<string, { label: string; todo: string }> = {
  'alert:red': { label: 'Red alerts', todo: 'Open each patient’s alert task first: these are urgent, like a BP crisis.' },
  'alert:yellow': { label: 'Orange alerts', todo: 'Review each patient’s alert task, like BP stage 2 or missed readings.' },
  'engagement:unread': { label: 'Unread messages', todo: 'Answer these patients in Engage.' },
  'engagement:quiet_7d': { label: 'No reading 7 days', todo: 'Ask for readings: no BP or weight reading in 7 days.' },
  'engagement:stuck_onboarding': { label: 'Stuck in onboarding', todo: 'No first reading more than 7 days into care: help with the device setup.' },
};

export interface BoardTodo { key: BoardPickKey; value: string; label: string; count: number | null; text: string }

// Exactly one board pick → what that list is for; also covers a value nothing on the board can un-press.
export function boardTodo(selected: Selected, filters: unknown[] | undefined): BoardTodo | null {
  const picks = BOARD_PICK_KEYS.flatMap((key) => (selected[key] ?? []).map((value) => ({ key, value })));
  const [first] = picks;
  if (picks.length !== 1 || !first) return null;
  const { key, value } = first;
  let label: string | undefined;
  let text: string | undefined;
  let count: number | null = null;
  if (key === 'open_task') {
    const facet = openTaskFacet(filters);
    const def = CHIP_DEFS.get(value);
    const group = facet?.groups.find((x) => x.value === value);
    const option = facet?.options.find((x) => x.value === value);
    label = def?.label ?? group?.label ?? option?.label;
    text = def?.todo;
    count = group?.count ?? option?.count ?? (facet ? 0 : null);
  } else if (key === 'report_ready') {
    const def = CHIP_DEFS.get(READY_CHIP);
    label = def?.label;
    count = reportReadyCount(filters);
    // Without the facet apiv2 cannot filter on it either: say so rather than "file the report now".
    text = count === null ? 'Not applied: ready reports are not available yet, so the list is not filtered.' : def?.todo;
  } else if (key === 'oap_outcome') {
    const copy = OAP_OUTCOME_LABELS[value as OapOutcome];
    const options = facetOptions(filters, key);
    label = copy?.label;
    // Without the facet apiv2 cannot filter on it either, so the list below is the whole roster.
    text = options ? copy?.todo : 'Not applied: CMS outcomes are not available yet, so the list is not filtered.';
    count = options ? options.find((x) => x.value === value)?.count ?? 0 : null;
  } else {
    const copy = TILE_TODO[`${key}:${value}`];
    const option = facetOptions(filters, key)?.find((x) => x.value === value);
    label = copy?.label ?? option?.label;
    text = copy?.todo;
    count = option?.count ?? null;
  }
  return {
    key, value, label: label ?? value, count, text: text ?? 'Patients with this filter.',
  };
}

// ── Roster opt-in (clinical_include=roster): Ready to submit, the sub-track facet, the columns ──

export interface SubtrackFacet {
  options: { value: string; count: number; group: string | null }[];
  groups: { value: string; label: string; count: number }[];
}

// Null without the facet: the toolbar draws no sub-track menu then.
export function subtrackFacet(filters: unknown[] | undefined): SubtrackFacet | null {
  if (!Array.isArray(filters)) return null;
  const f = filters.find((x) => isObj(x) && x.key === 'subtrack');
  if (!isObj(f) || !Array.isArray(f.options)) return null;
  return {
    options: f.options.filter(isObj).map((opt) => ({
      value: String(opt.value),
      count: typeof opt.count === 'number' ? opt.count : 0,
      group: typeof opt.group === 'string' ? opt.group : null,
    })),
    groups: (Array.isArray(f.groups) ? f.groups : []).filter(isObj).map((x) => ({
      value: String(x.value),
      label: typeof x.label === 'string' ? x.label : String(x.value),
      count: typeof x.count === 'number' ? x.count : 0,
    })),
  };
}

// apiv2's `yellow` alert reads Orange on the board: orange means an alert and nothing else.
const OPTION_LABELS: Record<string, Record<string, string>> = { alert: { yellow: 'Orange' } };

export function filterOptionLabel(key: string, value: string, label: string | undefined): string {
  return OPTION_LABELS[key]?.[value] ?? label ?? value;
}

// The facets the toolbar's generic filter menu must not draw: each has its own control.
export const OWN_CONTROL_FACETS = ['oap_outcome', 'subtrack', 'report_ready'] as const;

export interface BoardColumn {
  key: string;
  label: string;
  unit?: string;
  flex?: string;
  sortKey: string | null;
  hint?: string;
}

// The board's columns are the app's (spec dashboard-roster-columns §2.1); `sortKey` is what a header
// click sends, null = apiv2 has no order for it.
const BOARD_COLUMN_DEFS: Record<string, BoardColumn> = {
  name: {
    key: 'name', label: 'Patient', flex: '1.6fr', sortKey: 'name',
  },
  subtrack: {
    key: 'subtrack', label: 'Sub-track', flex: '1.2fr', sortKey: null,
  },
  conditions: {
    key: 'conditions', label: 'Conditions', flex: '1.1fr', sortKey: 'conditions',
  },
  alert: {
    key: 'alert', label: 'Alert status', flex: '1.2fr', sortKey: 'alert',
  },
  oap: {
    key: 'oap', label: 'Goals met', flex: '1.1fr', sortKey: null,
  },
  bp: {
    key: 'bp', label: 'BP', unit: 'mmHg', flex: '0.9fr', sortKey: 'bp',
  },
  next_due: {
    key: 'next_due', label: 'Next due', flex: '1.3fr', sortKey: 'next_due',
  },
  task: {
    key: 'task', label: 'Next task', flex: '1.3fr', sortKey: 'task',
  },
  overdue: {
    key: 'overdue', label: 'Overdue', flex: '0.6fr', sortKey: 'overdue',
  },
  lastReading: {
    key: 'lastReading', label: 'Last reading', flex: '0.8fr', sortKey: 'lastReading',
  },
  visit: {
    key: 'visit', label: 'Virtual visit', flex: '1fr', sortKey: 'visit_last', hint: 'Sorted by the last completed visit',
  },
  outreach: {
    key: 'outreach', label: 'Last outreach', flex: '0.9fr', sortKey: 'lastHumanOutreach', hint: 'Care team, then AI. Sorted by the care team’s',
  },
  enrolled: {
    key: 'enrolled', label: 'Enrolled', flex: '0.8fr', sortKey: 'enrolled',
  },
};

const BOARD_LAYOUT = {
  care_coordinator: ['name', 'subtrack', 'alert', 'oap', 'bp', 'next_due', 'lastReading', 'visit', 'outreach', 'enrolled'],
  clinician: ['name', 'subtrack', 'alert', 'oap', 'bp', 'next_due', 'lastReading', 'visit'],
};

// Without the roster the board keeps what apiv2 can fill today.
const WITHOUT_ROSTER: Record<string, string[]> = {
  subtrack: ['conditions'], next_due: ['task', 'overdue'], visit: [],
};

// Decided once per response from what apiv2 says it honoured, never from the rows (an empty page must
// not change the header). Goals met also follows apiv2's own column, for an apiv2 that predates the echo.
export function boardColumns(
  serverColumns: unknown[] | null | undefined,
  includes: readonly string[] = [],
  role: string | null | undefined = 'care_coordinator',
): BoardColumn[] | null {
  if (!Array.isArray(serverColumns)) return null;
  const roster = includes.includes('roster');
  const oap = includes.includes('oap') || serverColumns.some((c) => isObj(c) && c.key === 'oap');
  const layout = role === 'clinician' ? BOARD_LAYOUT.clinician : BOARD_LAYOUT.care_coordinator;
  return layout
    .flatMap((key) => (roster ? [key] : WITHOUT_ROSTER[key] ?? [key]))
    .filter((key) => oap || key !== 'oap')
    .flatMap((key) => {
      const def = BOARD_COLUMN_DEFS[key];
      return def ? [def] : [];
    });
}

function readIncludes(v: unknown): string[] {
  let list: unknown[] = [];
  if (Array.isArray(v)) list = v;
  else if (typeof v === 'string') list = v.split(',');
  return list.filter((x): x is string => typeof x === 'string').map((x) => x.trim()).filter(Boolean);
}

export function adaptDashboardEnvelope(
  env: DashboardEnvelope | null | undefined,
  role: string,
): LegacyDashboardShape {
  if (!env || env.status !== 'success') {
    return { status: 'error', reason: env?.error?.message || 'unknown error' };
  }
  const data = env.data || {};
  const meta = env.meta || {};
  const nowMs = Date.now();
  // Drop id-less rows: an empty userid is unselectable and would collide on
  // React keys / selection lookup. apiv2 always emits id_dev_user, so this only
  // bites a genuinely malformed row.
  const rows = (data.patients || [])
    .map((p) => adaptRow(p, nowMs))
    .filter((r) => r.userid !== '');
  return {
    status: 'success',
    role,
    kpis: adaptKpis(data.kpis || {}),
    columns: data.columns,
    filters: data.filters || [],
    rows,
    includes: readIncludes(meta.include),
    pagination: {
      total_count: meta.count ?? rows.length,
      offset: meta.offset ?? 0,
      limit: meta.limit ?? rows.length,
      has_more: (meta.offset ?? 0) + rows.length < (meta.count ?? rows.length),
    },
  };
}
